// src/components/resources/quizzes/components/FeedbackDisplay.tsx

'use client'

import type { ReactNode } from 'react'
import { cn } from '~/lib/utils'
import RiCheckLine from '~/icons/remix/generated/RiCheckLine'
import RiCloseLine from '~/icons/remix/generated/RiCloseLine'
import type { QuizQuestion } from '~/lib/resources/types/tools/quiz'

interface FeedbackDisplayProps {
  question: QuizQuestion
  isCorrect: boolean
  selectedAnswer: string | string[]
  className?: string
}

export function FeedbackDisplay({
  question,
  isCorrect,
  selectedAnswer,
  className
}: FeedbackDisplayProps): ReactNode {
  // Get the appropriate feedback explanation
  const getExplanation = (): string => {
    if (!question.explanation) return ''

    if (isCorrect) {
      return question.explanation.correct
    }

    // For single answer questions
    if (typeof selectedAnswer === 'string') {
      return question.explanation.incorrect?.[selectedAnswer] ?? question.explanation.incorrect?.default ?? ''
    }

    // For multiselect/ordered questions, use default incorrect message
    return question.explanation.incorrect?.default ?? ''
  }

  const explanation = getExplanation()
  if (!explanation) return null

  return (
    <div className={cn('mt-6', className)}>
      <div className={cn(
        "rounded-lg p-4",
        isCorrect 
          ? "bg-green-50 dark:bg-green-950/30" 
          : "bg-red-50 dark:bg-red-950/30"
      )}>
        <div className="mb-2 flex items-center gap-2">
          <div className={cn(
            "flex h-5 w-5 items-center justify-center rounded-full",
            isCorrect 
              ? "bg-green-500 dark:bg-green-400" 
              : "bg-red-500 dark:bg-red-400"
          )}>
            {isCorrect 
              ? <RiCheckLine className="h-3 w-3 text-white" />
              : <RiCloseLine className="h-3 w-3 text-white" />
            }
          </div>
          <h4 className={cn(
            "font-semibold",
            isCorrect 
              ? "text-green-900 dark:text-green-100" 
              : "text-red-900 dark:text-red-100"
          )}>
            {isCorrect ? 'Correct!' : 'Incorrect'}
          </h4>
        </div>
        <p className={cn(
          "text-sm",
          isCorrect 
            ? "text-green-800 dark:text-green-200" 
            : "text-red-800 dark:text-red-200"
        )}>
          {explanation}
        </p>
      </div>
    </div>
  )
} 